export type FocusMove = "next" | "prev" | "first" | "last";

/** Next focusable index moving by `step` (wraps), or the first/last one. */
export function nextIndex(count: number, current: number, move: FocusMove): number {
  if (count <= 0) return -1;
  if (move === "first") return 0;
  if (move === "last") return count - 1;
  if (current < 0) return move === "next" ? 0 : count - 1;
  return (current + (move === "next" ? 1 : -1) + count) % count;
}

const COMBINING_MARKS = /[\u0300-\u036f]/g;

function normalize(text: string): string {
  return text.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase();
}

/** Typeahead: the next item (after `current`) whose label starts with `char`. */
export function typeaheadIndex(labels: string[], current: number, char: string): number {
  const c = normalize(char);
  for (let step = 1; step <= labels.length; step++) {
    const i = (current + step + labels.length) % labels.length;
    if (normalize(labels[i] ?? "").startsWith(c)) return i;
  }
  return -1;
}

const MOVE_BY_KEY: ReadonlyMap<string, FocusMove> = new Map<string, FocusMove>([
  ["ArrowDown", "next"],
  ["ArrowUp", "prev"],
  ["Home", "first"],
  ["PageUp", "first"],
  ["End", "last"],
  ["PageDown", "last"],
]);

/** The focus move a navigation key asks for, if it is one. */
export function focusMoveForKey(key: string): FocusMove | undefined {
  return MOVE_BY_KEY.get(key);
}

/** A printable character typed without a command modifier: it drives the typeahead. */
export function isTypeaheadKey(e: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): boolean {
  return e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}
